import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserSignature } from '../../hooks/use-user-signature';
import { ConfirmingStep } from './components/ConfirmingStep';
import { DialogTitleContent } from './components/DialogTitleContent';
import { ErrorStep } from './components/ErrorStep';
import { SignatureStep } from './components/SignatureStep';
import { SuccessStep } from './components/SuccessStep';
import type { DialogStep, SignatureConfirmDialogProps } from './types';

export function SignatureConfirmDialog({
  open,
  onClose,
  onConfirm,
  recordCount,
  title,
  description,
  confirmationMessage,
}: SignatureConfirmDialogProps) {
  const { t } = useTranslation('signature');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const {
    signatureUrl,
    hasSignature,
    needsConfirmation,
    isLoading: isLoadingSignature,
    saveSignature,
    confirmSignature,
    reload,
  } = useUserSignature();

  const [step, setStep] = useState<DialogStep>('signature');
  const [error, setError] = useState<string | null>(null);
  const [isCreatingSignature, setIsCreatingSignature] = useState(false);

  const isConfirmingRef = useRef(false);

  const resetDialogState = useCallback(() => {
    setStep('signature');
    setError(null);
    setIsCreatingSignature(false);
    setIsConfirming(false);
    setIsConfirmed(false);
    isConfirmingRef.current = false;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload è stabile nell'hook reale (useCallback su queryClient); il mock dei test ne ricrea l'identità a ogni render
  useEffect(() => {
    if (open) {
      resetDialogState();
      // Ripesca la firma a ogni apertura: la GET /signature/me parte una sola
      // volta al page-load (dialog sempre montato) e se quella richiesta scade
      // o fallisce, "Conferma firma" non esisterebbe per nessun budget.
      void reload();
    }
  }, [open, resetDialogState]);

  const performConfirmSignature = useCallback(
    async (sigUrl: string | null | undefined) => {
      if (isConfirmingRef.current) {
        return false;
      }
      isConfirmingRef.current = true;
      setIsConfirming(true);
      setStep('confirming');

      try {
        // Non controllare `hasSignature` qui: questa callback può essere invocata
        // subito dopo saveSignature() con la closure del render precedente, dove
        // hasSignature è ancora false (firma appena creata). `sigUrl` basta.
        if (!sigUrl) {
          throw new Error(t('signatureMissingError'));
        }
        const result = await onConfirm(sigUrl);
        if (result === 'close') {
          // La pagina ha già deciso l'esito (§ F7, prompt NC immediato): chiude
          // il dialog senza renderizzare il passo di successo dentro un modale
          // già in chiusura.
          onClose();
        } else {
          setStep('success');
        }
        return true;
      } catch (err) {
        console.error('SignatureConfirmDialog error:', err);
        setStep('error');
        setIsConfirming(false);
        isConfirmingRef.current = false;
        const errorMessage = err instanceof Error ? err.message : t('signatureConfirmError');
        setError(errorMessage);
        return false;
      }
    },
    [onConfirm, onClose, t],
  );

  const handleSaveSignature = useCallback(
    async (dataUrl: string) => {
      try {
        const saved = await saveSignature(dataUrl);
        setIsCreatingSignature(false);
        setError(null);
        // Confirm with the URL returned by the save (presigned storage URL),
        // not the raw canvas dataUrl: downstream record endpoints reject
        // data: URLs, and this value is immune to the stale hasSignature
        // closure that motivated the pre-save flow.
        await performConfirmSignature(saved.signatureUrl);
      } catch {
        setError(t('signatureSaveError'));
      }
    },
    [saveSignature, performConfirmSignature, t],
  );

  const handleConfirm = useCallback(() => {
    if (!hasSignature) {
      setError(t('signatureMissingError'));
      return;
    }
    if (needsConfirmation) {
      // Best-effort, fire-and-forget: signing THIS record with the existing
      // signature is itself the dated act of will that closes the
      // "replicated, unconfirmed" evidentiary gap (Fase 1 mitigation). A
      // transient failure here must never block the record signature —
      // needsConfirmation simply stays true and the notice reappears next
      // time, which is a safe, self-healing degradation.
      void confirmSignature().catch(() => undefined);
    }
    performConfirmSignature(signatureUrl);
  }, [hasSignature, needsConfirmation, signatureUrl, confirmSignature, performConfirmSignature, t]);

  // Handle Enter key for signature and success steps
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }

      if (step === 'signature' && hasSignature && !isCreatingSignature) {
        event.preventDefault();
        handleConfirm();
      }

      if (step === 'success') {
        event.preventDefault();
        onClose();
      }
    };

    if (
      open &&
      (step === 'success' || (step === 'signature' && hasSignature && !isCreatingSignature))
    ) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [step, open, onClose, handleConfirm, hasSignature, isCreatingSignature]);

  const handleClose = useCallback(() => {
    if (step === 'confirming') {
      return;
    }
    onClose();
  }, [step, onClose]);

  const renderDialogContent = () => {
    if (isLoadingSignature) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (step) {
      case 'signature':
        return (
          <SignatureStep
            description={description}
            recordCount={recordCount}
            error={error}
            hasSignature={hasSignature}
            signatureUrl={signatureUrl}
            isCreatingSignature={isCreatingSignature}
            allowEdit={false}
            needsConfirmation={needsConfirmation}
            confirmationMessage={confirmationMessage}
            isConfirmed={isConfirmed}
            onConfirmationChange={setIsConfirmed}
            onSaveSignature={handleSaveSignature}
            onStartCreating={() => setIsCreatingSignature(true)}
            onCancelCreating={() => setIsCreatingSignature(false)}
          />
        );
      case 'confirming':
        return <ConfirmingStep />;
      case 'success':
        return <SuccessStep />;
      case 'error':
        return <ErrorStep error={error} />;
      default:
        return null;
    }
  };

  const renderDialogActions = () => {
    // Step signature: mostra "Conferma firma" solo se ha firma valida (URL caricabile) e non sta creando.
    // Se la firma è registrata ma l'URL manca, l'utente deve ridisegnarla tramite il pad mostrato
    // dallo SignatureStep, quindi nascondiamo il "Conferma firma" finché non c'è una firma utilizzabile.
    if (step === 'signature' && hasSignature && !isCreatingSignature && signatureUrl) {
      const confirmationRequired = !!confirmationMessage;
      const confirmationBlocked = confirmationRequired && !isConfirmed;
      return (
        <>
          <Button onClick={handleClose}>{t('cancelButton')}</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!hasSignature || isConfirming || confirmationBlocked}
          >
            {t('confirmSignature')}
          </Button>
        </>
      );
    }

    // Step success o error: solo chiudi
    if (step === 'success' || step === 'error') {
      return (
        <Button variant="contained" onClick={handleClose}>
          {t('close', { ns: 'common' })}
        </Button>
      );
    }

    // Fallback: nello step firma quando l'utente sta disegnando o deve ridisegnare,
    // mostra almeno un pulsante Annulla (il salvataggio avviene dal SignaturePad).
    if (step === 'signature') {
      return <Button onClick={handleClose}>{t('cancelButton')}</Button>;
    }

    return null;
  };

  const dialogTitle = title ?? t('confirmSignature');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DialogTitleContent step={step} title={dialogTitle} />
      </DialogTitle>

      <DialogContent>{renderDialogContent()}</DialogContent>

      <DialogActions>{renderDialogActions()}</DialogActions>
    </Dialog>
  );
}
